import { useEffect, useState } from "react";

interface Props {
  /** Font size in px for the wordmark (default 12 — footer use). */
  size?: number;
  /** CSS color for the letters. Defaults to currentColor so it inherits. */
  color?: string;
  /** CSS color for the cursor block. Defaults to var(--color-accent). */
  cursor?: string;
  /** Background color the letter flips to when the block is visible. */
  letterBg?: string;
}

/**
 * The `martzmakes` wordmark — JetBrains Mono semi-bold with a blinking
 * cursor block sitting behind the second `m`. While the block is visible
 * the letter underneath flips to the page background, mimicking a terminal
 * cursor over text. Per the martzmakes design system brand wordmark.
 */
export function MartzmakesWordmark({
  size = 12,
  color = "currentColor",
  cursor = "var(--color-accent)",
  letterBg = "var(--color-bg-0)",
}: Props) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setOn((v) => !v), 550);
    return () => clearInterval(id);
  }, []);

  const letterStyle = {
    fontFamily: "var(--font-mono)",
    fontSize: size,
    fontWeight: 600,
    letterSpacing: `${-0.03 * size}px`,
    color,
    lineHeight: 1.1,
  } as const;
  const blockWidth = size * 0.54;
  const blockHeight = size * 1.05;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        verticalAlign: "baseline",
      }}
    >
      <span style={letterStyle}>martz</span>
      <span
        style={{
          width: blockWidth,
          height: blockHeight,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <span
          style={{
            width: blockWidth,
            height: blockHeight,
            background: on ? cursor : "transparent",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
        <span
          style={{
            ...letterStyle,
            color: on ? letterBg : color,
            letterSpacing: 0,
            position: "relative",
          }}
        >
          m
        </span>
      </span>
      <span style={letterStyle}>akes</span>
    </span>
  );
}

export function MartzmakesWordmarkLink({ size = 12 }: { size?: number }) {
  return (
    <a
      href="https://martzmakes.com"
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "none", display: "inline-flex" }}
      aria-label="martzmakes"
    >
      <MartzmakesWordmark size={size} />
    </a>
  );
}
